import { createApi } from '@reduxjs/toolkit/query/react';
import dynamicBaseQueryWithLogger from '@/services/utils/dynamicBaseQueryWithLogger';
import { handlerErrorResponse, handleUnwrapRTKError } from '@/services/utils/handlers';
import { setAppNotice } from '@/app/store/appShellSlice';
import type { ServiceOrderItem } from '@/services/reparoly/ordersApi';

const BASE_URL = process.env.NEXT_PUBLIC_REPAROLY_API_URL ?? 'https://api.reparoly.local';

export interface ApiResponseBase<T> {
  success: boolean;
  message: string;
  data: T;
}

export type RegisterIntakeDeviceInput = {
  imei_serie: string;
  diagnostic: string;
  reported_failure: string;
  // order_status_id?: number; // omitido segun instruccion del flujo
  has_lock?: boolean;
  lock_type?: number | null;
  lock_info?: string | null;
  warranty_id: number;
  repair_cost: number;
  total_cost: number;
  paid_amount: number;
  equipment_type_id: number;
  equipment_model_id: number;
  equipment_brand_id: number;
};

export type RegisterIntakeInput = {
  client_id: number;
  notes?: string | null;
  devices: RegisterIntakeDeviceInput[];
};

export type RegisterIntakeResult = {
  intake_id: number;
  intake_code: string;
  orders: { id: number; order_code: string }[];
};

// Intake ticket data (OpenAPI: IntakeTicketData)
export interface IntakeTicketData {
  pdf_base64: string;
  pdf_url?: string | null;
  intake_id: number;
  intake_code: string;
  orders_count: number;
  total_cost: number;
  total_paid: number;
  total_balance: number;
  print_id: number;
}

export const intakesApi = createApi({
  reducerPath: 'intakesApi',
  baseQuery: dynamicBaseQueryWithLogger(BASE_URL),
  endpoints: (builder) => ({
    // GET /v1/intakes/search/{code}
    getIntakeByCode: builder.query<
      ApiResponseBase<{
        intake_id: number;
        intake_code: string;
        intake_created_at: string;
        intake_updated_at: string;
        items: ServiceOrderItem[];
      }>,
      string
    >({
      query: (code) => ({ url: `/v1/intakes/search/${encodeURIComponent(code)}`, method: 'GET' }),
      transformErrorResponse: handlerErrorResponse('Intakes.SearchByCode'),
    }),
    // POST /v1/intakes/register
    registerIntake: builder.mutation<ApiResponseBase<RegisterIntakeResult>, RegisterIntakeInput>({
      query: (body) => ({ url: '/v1/intakes', method: 'POST', body }),
      transformErrorResponse: handlerErrorResponse('Intakes.Register'),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          const e = handleUnwrapRTKError(err);
          dispatch(
            setAppNotice({
              message: e?.message || 'Error al registrar intake',
              status: e?.code ?? null,
              details: `Endpoint: ${e?.endpointName ?? 'Intakes.Register'}${e?.code ? ` — Codigo: ${e.code}` : ''}`,
              messageFormatted: '',
              action: 'intakes.register',
              endpointName: e?.endpointName || 'Intakes.Register',
              validationErrors: (e as any)?.details ?? (e as any)?.originalError?.errors ?? {},
            }),
          );
        }
      },
    }),

    // POST /v1/intakes/{id}/ticket/print
    printIntakeTicket: builder.mutation<
      ApiResponseBase<IntakeTicketData>,
      { id: number | string; body?: { signature?: string | null } }
    >({
      query: ({ id, body }) => ({ url: `/v1/intakes/${id}/ticket/print`, method: 'POST', body }),
      transformErrorResponse: handlerErrorResponse('Intakes.TicketPrint'),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          const e = handleUnwrapRTKError(err);
          dispatch(
            setAppNotice({
              message: e?.message || 'Error al generar el ticket de intake',
              status: e?.code ?? null,
              details: `Endpoint: ${e?.endpointName ?? 'Intakes.TicketPrint'}${
                e?.code ? ` — Codigo: ${e.code}` : ''
              }`,
              messageFormatted: '',
              action: 'intakes.ticket.print',
              endpointName: e?.endpointName || 'Intakes.TicketPrint',
              validationErrors: (e as any)?.details ?? (e as any)?.originalError?.errors ?? {},
            }),
          );
        }
      },
    }),
  }),
});

export const {
  useRegisterIntakeMutation,
  useGetIntakeByCodeQuery,
  useLazyGetIntakeByCodeQuery,
  usePrintIntakeTicketMutation,
} = intakesApi;
